import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaHeart, FaGithub, FaLinkedin, FaTwitter, FaEnvelope, FaArrowUp } from 'react-icons/fa';

const Footer = ({ currentTheme }) => {
  const [isVisible, setIsVisible] = useState(false);
  const currentYear = new Date().getFullYear();

  const footerAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const heartAnimation = {
    scale: [1, 1.2, 1],
    transition: {
      duration: 0.8,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const socialHover = {
    scale: 1.2,
    rotate: 5,
    transition: { duration: 0.2 }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <>
      {isVisible && (
        <div 
          style={{
            position: 'fixed', 
            bottom: '20px', 
            right: '20px', 
            zIndex: 9999
          }}
        >
          <motion.button
            onClick={scrollToTop}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(45deg, ${currentTheme.primary}, ${currentTheme.secondary})`,
              border: 'none',
              boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
              color: currentTheme.background
            }}
          >
            <motion.div
              animate={{
                y: [0, -5, 0],
                transition: { 
                  duration: 1.5, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }
              }}
            >
              <FaArrowUp size={24} />
            </motion.div>
          </motion.button>
        </div>
      )}

      <footer className="py-5 position-relative" style={{ backgroundColor: currentTheme.background, color: currentTheme.text }}>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={footerAnimation}
          >
            <Row className="align-items-center mb-4">
              <Col xs={12} md={4} className="text-center text-md-start mb-3 mb-md-0">
                <motion.p 
                  className="mb-0 fs-6"
                  variants={footerAnimation}
                >
                  © {currentYear} All Rights Reserved
                </motion.p>
              </Col>

              <Col xs={12} md={4} className="text-center mb-3 mb-md-0">
                <motion.div 
                  className="made-with-love"
                  variants={footerAnimation}
                >
                  <span className="fs-6">Crafted with</span>
                  <motion.span
                    className="mx-2 text-danger"
                    animate={heartAnimation}
                  >
                    <FaHeart />
                  </motion.span>
                  <span className="fs-6">by</span>
                  <span className="ms-2 fw-bold" style={{ color: currentTheme.primary }}>Sylvester Omondi</span>
                </motion.div>
              </Col>

              <Col xs={12} md={4}>
                <motion.div
                  className="d-flex justify-content-center justify-content-md-end gap-4"
                  variants={footerAnimation}
                >
                  {[
                    { Icon: FaGithub, href: "https://github.com/OMONDISOFTWAREENGINEER" },
                    { Icon: FaLinkedin, href: "https://www.linkedin.com/in/sylvester-omondi-2868b8219/" },
                    { Icon: FaTwitter, href: "https://x.com/sylvest34071957" },
                    { Icon: FaEnvelope, href: "mailto:Somondi398@gmail.com" },
                  ].map(({ Icon, href }, index) => (
                    <motion.a
                      key={index}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fs-5"
                      whileHover={socialHover}
                      style={{
                        backgroundColor: `rgba(${currentTheme.primary}, 0.1)`,
                        padding: '10px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: currentTheme.primary
                      }}
                    >
                      <Icon />
                    </motion.a>
                  ))}
                </motion.div>
              </Col>
            </Row>

            <motion.div 
              className="text-center mt-4"
              variants={footerAnimation}
            >
              <p className="mb-0 small" style={{ color: currentTheme.text }}>
                <span className="fw-bold" style={{ color: currentTheme.primary }}>Transforming Ideas</span> into 
                Digital Reality | Frontend Developer | Technical Writer | Data Scientist | CCTV Specialist
              </p>
            </motion.div>
          </motion.div>
        </Container>

        <div 
          className="position-absolute bottom-0 start-0 w-100" 
          style={{ height: '4px', background: `linear-gradient(90deg, ${currentTheme.primary}, ${currentTheme.secondary})` }}
        ></div>
      </footer>
    </>
  );
};

export default Footer;